// src/pages/FundPlanner/components/FundStructurer/CashflowWizard.jsx
import React, { useState, useEffect, useMemo } from "react";
import dayjs from "lib/dayjs";
import { DateInput, DropdownSelect, NumberInput } from "components/InputStyled";
import { generateCashflows } from "pages/FundPlanner/lib/fundMath";

/**
 * CashflowWizard
 * Generates draw schedule based on user parameters.
 * Total Amount is shared with FundSetup through StructurerContext.
 *
 * leadDays removed
 * maturityDate removed
 */

// ──────────────────────────────────────────────
// Defaults
// ──────────────────────────────────────────────
const DEFAULT_PERIODS = 12;

export default function CashflowWizard({
  mode = "gross",
  settlementDate,
  totalAmount,
  setTotalAmount,
  onGenerate,
  onCancel,
}) {
  // ──────────────────────────────────────────────
  // 1️⃣ Session persistence keys
  // ──────────────────────────────────────────────
  const STORAGE_KEY = "fundplanner.cashflowwizard";

  const loadStoredParams = () => {
    try {
      const raw = sessionStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : {};
    } catch {
      return {};
    }
  };

  const saveStoredParams = (data) => {
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    } catch {
      /* ignore */
    }
  };

  // ──────────────────────────────────────────────
  // 2️⃣ Draw schedule parameters (restored from session)
  // ──────────────────────────────────────────────
  const baseDate = dayjs(settlementDate);
  const stored = loadStoredParams();

  const defaultStart = baseDate.add(1, "month").format("YYYY-MM-DD");
  const defaultEnd = baseDate
    .add(DEFAULT_PERIODS, "month")
    .format("YYYY-MM-DD");

  const [startDate, setStartDate] = useState(stored.startDate || defaultStart);
  const [frequency, setFrequency] = useState(stored.frequency || "monthly");

  const inferredMode =
    stored.terminationMode || (stored.endDate ? "endDate" : "periods");

  const [terminationMode, setTerminationMode] = useState(inferredMode);

  const [endDate, setEndDate] = useState(stored.endDate || defaultEnd);

  const [numPeriods, setNumPeriods] = useState(() => {
    if (stored.terminationMode === "endDate") return "";
    if (typeof stored.numPeriods === "number") return stored.numPeriods;
    return DEFAULT_PERIODS;
  });

  const [distribution, setDistribution] = useState(
    stored.distribution || "level"
  );

  // ──────────────────────────────────────────────
  // 3️⃣ Persist to sessionStorage
  // ──────────────────────────────────────────────
  useEffect(() => {
    saveStoredParams({
      startDate,
      frequency,
      terminationMode,
      endDate: terminationMode === "endDate" ? endDate : "",
      numPeriods:
        terminationMode === "periods" ? Number(numPeriods) || 1 : "",
      distribution,
    });
  }, [startDate, frequency, terminationMode, endDate, numPeriods, distribution]);

  useEffect(() => {
    if (!stored.startDate) {
      const s = dayjs(settlementDate);
      setStartDate(s.add(1, "month").format("YYYY-MM-DD"));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settlementDate]);

  // ──────────────────────────────────────────────
  // 4️⃣ Helpers and derived values
  // ──────────────────────────────────────────────
  const freqMap = useMemo(
    () => ({
      monthly: { unit: "month", step: 1 },
      quarterly: { unit: "month", step: 3 },
      semiannual: { unit: "month", step: 6 },
      annual: { unit: "year", step: 1 },
    }),
    []
  );

  const { unit, step } = freqMap[frequency] || freqMap.monthly;

  const endBeforeStart = useMemo(() => {
    if (terminationMode !== "endDate") return false;
    return dayjs(endDate).isBefore(dayjs(startDate), "day");
  }, [terminationMode, startDate, endDate]);

  const deriveNumPeriodsFromEndDate = (sISO, eISO) => {
    const s = dayjs(sISO);
    const e = dayjs(eISO);

    if (!s.isValid() || !e.isValid() || e.isBefore(s, "day")) return 1;

    let n = 1;
    let d = s;

    for (let i = 0; i < 10000; i += 1) {
      const next = d.add(step, unit);

      if (next.isAfter(e, "day")) break;

      d = next;
      n += 1;
    }

    return Math.max(1, n);
  };

  const effectiveNumPeriods = useMemo(() => {
    return terminationMode === "endDate"
      ? deriveNumPeriodsFromEndDate(startDate, endDate)
      : Math.max(1, Number(numPeriods) || 1);
  }, [terminationMode, startDate, endDate, numPeriods]);

  const finalDrawDate = useMemo(() => {
    return dayjs(startDate)
      .add((effectiveNumPeriods - 1) * step, unit)
      .format("YYYY-MM-DD");
  }, [startDate, effectiveNumPeriods, step, unit]);

  // ──────────────────────────────────────────────
  // 5️⃣ Input handlers
  // ──────────────────────────────────────────────
  const handleTotalAmountChange = (value) => {
    setTotalAmount?.(value);
  };

  const handleEndDateChange = (v) => {
    setTerminationMode("endDate");
    setEndDate(v);
    setNumPeriods("");
  };

  const handleNumPeriodsChange = (n) => {
    setTerminationMode("periods");
    setNumPeriods(Math.max(1, Number(n) || 1));
    setEndDate("");
  };

  // ──────────────────────────────────────────────
  // 6️⃣ Generate
  // ──────────────────────────────────────────────
  const canGenerate =
    Number(totalAmount) > 0 &&
    (!endBeforeStart || terminationMode !== "endDate") &&
    effectiveNumPeriods >= 1;

  const handleGenerate = () => {
    if (!canGenerate) return;

    const flows = generateCashflows({
      mode,
      startDate,
      frequency,
      numPeriods: effectiveNumPeriods,
      totalAmount: Number(totalAmount),
      distribution,
    });

    const drawSchedule = flows.map((f) => ({
      id: f.id,
      drawDate: f.drawDate,
      drawRequirement: f.inflow || f.outflow || f.amount || 0,
    }));

    const totalDraws = drawSchedule.reduce(
      (s, r) => s + (r.drawRequirement || 0),
      0
    );

    const diff = Number(totalAmount) - totalDraws;

    if (Math.abs(diff) > 0.005 && drawSchedule.length) {
      drawSchedule[drawSchedule.length - 1].drawRequirement += diff;
    }

    onGenerate?.(drawSchedule);
  };

  // ──────────────────────────────────────────────
  // 7️⃣ Render
  // ──────────────────────────────────────────────
  const endDateWrapCls = terminationMode === "endDate" ? "" : "opacity-70";
  const periodsWrapCls = terminationMode === "periods" ? "" : "opacity-70";

  return (
    <div className="space-y-5">
      <div className="pb-3 border-b border-border">
        <h3 className="text-lg font-semibold text-app">
          Cash Flow Wizard
        </h3>
      </div>

      {/* Amount row */}
      <div>
        <NumberInput
          label="Gross Cash Flow Requirement ($)"
          value={totalAmount}
          onChange={handleTotalAmountChange}
          className="w-56"
        />
      </div>

      {/* Start/Frequency row */}
      <div className="flex flex-wrap gap-4 items-end">
        <DateInput
          label="Start Date"
          value={startDate}
          onChange={setStartDate}
        />

        <DropdownSelect
          label="Frequency"
          value={frequency}
          onChange={setFrequency}
          options={[
            { value: "monthly", label: "Monthly" },
            { value: "quarterly", label: "Quarterly" },
            { value: "semiannual", label: "Semiannual" },
            { value: "annual", label: "Annual" },
          ]}
        />
      </div>

      <div className="flex flex-wrap gap-4 items-end">
        <div className={endDateWrapCls}>
          <DateInput
            label="End Date"
            value={endDate}
            onChange={handleEndDateChange}
          />
        </div>

        <div className="pb-2 px-1 text-muted text-xs select-none">OR</div>

        <div className={`w-28 ${periodsWrapCls}`}>
          <NumberInput
            label="Number of Periods"
            type="integer"
            value={numPeriods}
            onChange={handleNumPeriodsChange}
          />
        </div>
      </div>

      <div className="text-muted text-sm">
        Final date:{" "}
        <span className="font-medium text-app">{finalDrawDate}</span>

        {terminationMode === "endDate" ? (
          <span className="text-muted">
            {" "}
            (Periods derived: {effectiveNumPeriods})
          </span>
        ) : null}
      </div>

      <DropdownSelect
        label="Distribution"
        value={distribution}
        onChange={setDistribution}
        options={[
          { value: "level", label: "Level / Uniform" },
          { value: "normal", label: "Normal / Bell Curve" },
        ]}
      />

      <div className="flex gap-3 pt-2">
        <button
          type="button"
          onClick={handleGenerate}
          className="btn btn-primary"
          disabled={!canGenerate}
        >
          Generate Schedule
        </button>

        <button
          type="button"
          onClick={() => onCancel?.()}
          className="btn btn-ghost"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}